/**
 * Actionable trip suggestions derived from planning gaps. No fake todos.
 */

import { createId } from "./ids.ts";
import type { TripDocument } from "./schema.ts";

export type SuggestionAction =
  | { type: "navigate"; href: string; label: string }
  | { type: "save"; label: string; apply: (doc: TripDocument) => TripDocument };

export type TripSuggestion = {
  id: string;
  title: string;
  detail: string;
  action: SuggestionAction;
};

function hasBoatPlan(doc: TripDocument): boolean {
  return doc.events.some(
    (e) =>
      !e.archived &&
      (e.title?.toLowerCase().includes("boat") ||
        e.title?.toLowerCase().includes("ferry") ||
        e.notes.some((n) => n.toLowerCase().includes("boat"))),
  );
}

export function tripSuggestions(doc: TripDocument): TripSuggestion[] {
  const out: TripSuggestion[] = [];
  const dismissed = new Set(doc.reviewItems.filter((i) => i.dismissed).map((i) => i.id));

  const boat = doc.reviewItems.find((i) => i.id === "boats");
  if (boat && !boat.dismissed && !hasBoatPlan(doc)) {
    const date = doc.stays.find((s) => s.chapter === "Koh Samet")?.startDate ?? doc.trip.startDate;
    out.push({
      id: "suggest-boat",
      title: "Plan the boat crossing",
      detail: "The mainland van ends at the pier. Add a boat leg when you know the pier and time.",
      action: {
        type: "navigate",
        href: `/journey/day/${date}/add?kind=transfer`,
        label: "Add transfer",
      },
    });
  }

  const kasemReturn = doc.events.find((e) => e.id === "kasem-return");
  if (kasemReturn && !kasemReturn.plannedDeparture && !dismissed.has("kasem-return")) {
    out.push({
      id: "suggest-kasem-return",
      title: "Confirm return van time",
      detail: "6 November is inferred from hotel dates. Pickup time and pier are still open.",
      action: {
        type: "navigate",
        href: `/journey/day/${kasemReturn.date}/add?edit=${kasemReturn.id}`,
        label: "Edit transfer",
      },
    });
  }

  const food = doc.events.find((e) => e.id === "food-tour-event");
  if (food?.startConfidence?.includes("provisional") && !dismissed.has("food-meeting")) {
    out.push({
      id: "suggest-food-cash",
      title: "Cash for the food tour",
      detail: "THB 8,150 group price is on the booking. Keep baht ready for pay-on-day if needed.",
      action: {
        type: "save",
        label: "Add to checklist",
        apply: (d) => ({
          ...d,
          preparationItems: [
            ...d.preparationItems,
            {
              id: createId("prep"),
              title: "Cash ready for Chiang Mai food tour (THB 8,150 group)",
              notes: "From the accepted booking basis. Not a new total.",
              dueDate: food.date,
              linkedEntityIds: [food.id],
              ownerOnlyShared: true,
            },
          ],
        }),
      },
    });
  }

  if (!doc.trip.insuranceNote) {
    out.push({
      id: "suggest-insurance",
      title: "Add insurance details",
      detail: "Policy number and emergency line belong in Essentials, not on bookings.",
      action: { type: "navigate", href: "/essentials#insurance", label: "Open insurance" },
    });
  }

  const allergyPrep = doc.preparationItems.some((p) => p.id === "prep-allergy-card");
  if (!allergyPrep && doc.bookings.some((b) => (b.dietaryNotes?.length ?? 0) > 0)) {
    out.push({
      id: "suggest-allergy-card",
      title: "Review the allergy card",
      detail: "Operator-informed notes only. Open the card before food tours and copy for guides.",
      action: {
        type: "save",
        label: "Pin to checklist",
        apply: (d) => ({
          ...d,
          preparationItems: [
            ...d.preparationItems,
            {
              id: "prep-allergy-card",
              title: "Allergy card reviewed",
              notes: "Show Essentials allergy card to guides. Not a safety guarantee.",
              dueDate: null,
              linkedEntityIds: [],
              ownerOnlyShared: true,
            },
          ],
        }),
      },
    });
  }

  return out;
}

export function suggestionVisible(doc: TripDocument, suggestion: TripSuggestion): boolean {
  if (suggestion.id === "suggest-boat") return !hasBoatPlan(doc);
  if (suggestion.id === "suggest-kasem-return") {
    const e = doc.events.find((ev) => ev.id === "kasem-return");
    return Boolean(e && !e.plannedDeparture);
  }
  if (suggestion.id === "suggest-food-cash") {
    return !doc.preparationItems.some((p) => p.linkedEntityIds.includes("food-tour-event"));
  }
  if (suggestion.id === "suggest-insurance") return !doc.trip.insuranceNote;
  if (suggestion.id === "suggest-allergy-card") {
    return !doc.preparationItems.some((p) => p.id === "prep-allergy-card");
  }
  return true;
}

export function visibleSuggestions(
  doc: TripDocument,
  role: "family" | "owner" | null = "owner",
): TripSuggestion[] {
  return tripSuggestions(doc)
    .filter((s) => suggestionVisible(doc, s))
    .map((s) => {
      if (role === "owner") return s;
      if (s.action.type === "save") {
        return {
          ...s,
          action: { type: "navigate" as const, href: "/essentials", label: "Chris is arranging this" },
        };
      }
      if (
        s.action.type === "navigate" &&
        (s.action.href.includes("/add") || /^Add |^Edit /.test(s.action.label))
      ) {
        const href = s.action.href.replace(/\/add.*/, "").replace(/\?edit=.*/, "") || "/journey";
        return {
          ...s,
          action: { type: "navigate" as const, href, label: "Chris is arranging this" },
        };
      }
      if (s.action.type === "navigate" && s.action.href.startsWith("/essentials#")) {
        return { ...s, action: { ...s.action, href: "/essentials/emergency" } };
      }
      return s;
    });
}
